import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { ThankYouPreview } from "@/components/ThankYouPreview";
import { DesignFields, useDesignOptions } from "@/components/thankyou/DesignFields";
import { DesignPreview, sampleCtx } from "@/components/thankyou/designPreview";
import { useSharedSections } from "@/lib/useSharedSections";
import { PhoneDisplaySelect, hidesPhone } from "@/lib/phoneDisplay";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const TOGGLES = [
  ["show_logo", "Firm logo", "logo"],
  ["show_firm_name", "Firm name", "name"],
  ["show_call_text", "Tap-to-call & text buttons", "buttons"],
  ["show_timeline", "\u201cWhat happens next\u201d timeline", "timeline"],
  ["show_buyer_message", "Buyer's own thank-you message", "message"],
];
const INPUT = "mt-1 w-full border-2 border-black px-2 py-1.5 text-sm";
const BTN = "border-2 border-black px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40";

function toForm(d) {
  const s = d.settings;
  return {
    inherit: s.inherit,
    display: { ...s.display },
    headline: s.headline || "",
    message: s.message || "",
    custom_timeline: s.custom_timeline,
    timeline: s.timeline.map((t) => ({ ...t })),
    design: s.design || null,
    contact: { ...(s.contact || {}) },
    video_url: s.video_url || "",
    phone_display: d.page.phone_display || "both",
    thankyou_phone: d.page.thankyou_phone || "",
  };
}

const SwitchRow = ({ label, checked, disabled, onChange, testId }) => (
  <label className={`flex items-center justify-between gap-3 border-b border-zinc-200 py-2 text-sm ${disabled ? "opacity-50" : ""}`}>
    <span>{label}</span>
    <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} data-testid={testId} />
  </label>
);

const TimelineEditor = ({ form, setForm, defaults }) => {
  const setStep = (i, k, v) => setForm((f) => ({ ...f, timeline: f.timeline.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));
  return (
    <div className="mt-2 flex flex-col gap-2">
      {form.timeline.map((s, i) => (
        <div key={i} className="grid grid-cols-[1fr_2fr] gap-2">
          <input className={INPUT} value={s.title} maxLength={80} placeholder={`Step ${i + 1} title`}
                 onChange={(e) => setStep(i, "title", e.target.value)} data-testid={`admin-ty-step-${i}-title`} />
          <input className={INPUT} value={s.body} maxLength={240} placeholder="Short description"
                 onChange={(e) => setStep(i, "body", e.target.value)} data-testid={`admin-ty-step-${i}-body`} />
        </div>
      ))}
      <button type="button" className={`${BTN} self-start bg-white hover:bg-black hover:text-white`} data-testid="admin-ty-reset-timeline"
              onClick={() => setForm((f) => ({ ...f, timeline: defaults.timeline.map((t) => ({ ...t })) }))}>
        Reset to default steps
      </button>
    </div>
  );
};

const Controls = ({ data, form, setForm, options }) => {
  const isBuyer = data.page.kind === "buyer";
  const locked = isBuyer && form.inherit;
  const setDisplay = (k) => (v) => setForm((f) => ({ ...f, display: { ...f.display, [k]: v } }));
  return (
    <div>
      <DesignFields data={data} form={form} setForm={setForm} options={options} />
      {isBuyer && (
        <div className="mb-3 border-2 border-black bg-zinc-50 p-2">
          <SwitchRow label="Use each funnel's own settings" checked={form.inherit} testId="admin-ty-inherit"
                     onChange={(v) => setForm((f) => ({ ...f, inherit: v }))} />
          <div className="mt-1 text-[10.5px] text-zinc-600">Turn off to choose exactly what this buyer's thank-you page shows on every funnel.</div>
        </div>
      )}
      {isBuyer && (
        <label className="mb-3 block border-2 border-black p-2 text-sm" data-testid="admin-ty-phone-block">
          <span className="label-mono">Show phone number</span>
          <PhoneDisplaySelect value={form.phone_display} testId="admin-ty-phone-display"
            onChange={(v) => setForm((f) => ({ ...f, phone_display: v }))} />
          <span className="mt-1 block text-[10.5px] text-zinc-600">Covers all of this buyer&apos;s landing pages and thank-you pages. The number stays saved either way.</span>
          <span className="label-mono mt-3 block">Thank-you page phone (optional)</span>
          <input className={INPUT} value={form.thankyou_phone} maxLength={40} type="tel"
                 placeholder={data.page.phone ? `Leave blank to use ${data.page.phone}` : "e.g. an intake line"}
                 onChange={(e) => setForm((f) => ({ ...f, thankyou_phone: e.target.value }))} data-testid="admin-ty-thankyou-phone" />
          <span className="mt-1 block text-[10.5px] text-zinc-600">A different number for the thank-you page only, like an intake line. Landing pages keep the main number.</span>
        </label>
      )}
      <div className="label-mono">Show on this thank-you page</div>
      {locked ? (
        <div className="mt-2 border border-dashed border-zinc-400 p-3 text-[11px] text-zinc-600" data-testid="admin-ty-inherit-note">
          Follows each funnel&apos;s own switches and steps. Turn off the switch above to set them for this buyer.
        </div>
      ) : TOGGLES.map(([k, label, tid]) => (
        <SwitchRow key={k} label={label} checked={Boolean(form.display[k])}
                   onChange={setDisplay(k)} testId={`admin-toggle-${tid}`} />
      ))}
      <label className="label-mono mt-4 block">Headline (optional)
        <input className={INPUT} value={form.headline} maxLength={160} placeholder="Leave blank for the default"
               onChange={(e) => setForm((f) => ({ ...f, headline: e.target.value }))} data-testid="admin-ty-headline" />
      </label>
      <label className="label-mono mt-3 block">Buyer&apos;s own thank-you message
        <textarea className={`${INPUT} h-20 resize-none`} value={form.message} maxLength={1200}
                  placeholder="e.g. Thanks for trusting us. Our intake team answers 7 days a week."
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} data-testid="admin-ty-message" />
      </label>
      {!locked && (
        <div className="mt-3">
          <SwitchRow label="Custom &ldquo;what happens next&rdquo; steps" checked={form.custom_timeline}
                     onChange={(v) => setForm((f) => ({ ...f, custom_timeline: v }))} testId="admin-ty-custom-timeline" />
          {form.custom_timeline && <TimelineEditor form={form} setForm={setForm} defaults={data.defaults} />}
        </div>
      )}
    </div>
  );
};

const PagePreview = ({ data, form, options, shared }) => {
  const blocks = options.find((o) => o.key === form.design)?.blocks;
  const noPhone = hidesPhone(form.phone_display, "thank_you");
  const p = { ...data.page, phone: noPhone ? null : (form.thankyou_phone.trim() || data.page.phone) };
  if (!blocks?.length) {
    const shownForm = noPhone ? { ...form, display: { ...form.display, show_call_text: false } } : form;
    return <ThankYouPreview page={p} form={shownForm} defaults={data.defaults} />;
  }
  const ctx = sampleCtx(shared, {
    firm: p.firm_name, phone: p.phone, logoUrl: p.logo_url ? `${BACKEND}${p.logo_url}` : null,
    contact: form.contact, videoUrl: form.video_url, accent: p.brand_color,
    timeline: form.custom_timeline ? form.timeline : data.defaults.timeline,
    buyerCode: p.kind === "buyer" ? p.slug : null,
  });
  return <DesignPreview blocks={blocks} ctx={ctx} note={p.kind === "buyer" ? "Preview" : "Preview with a sample firm"} />;
};

export const ThankYouSettingsDialog = ({ pageId, onClose }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const options = useDesignOptions();
  const shared = useSharedSections();

  useEffect(() => {
    api.get(`/admin/thank-you/${pageId}`)
      .then((r) => { setData(r.data); setForm(toForm(r.data)); })
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Couldn't load thank-you settings");
        onClose();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/thank-you/${pageId}`, {
        inherit: form.inherit,
        display: form.display,
        headline: form.headline,
        message: form.message,
        timeline: form.custom_timeline ? form.timeline.filter((s) => s.title.trim()) : null,
        design: form.design || null,
        contact: form.contact,
        video_url: form.video_url.trim() || null,
        ...(data.page.kind === "buyer" ? { phone_display: form.phone_display, thankyou_phone: form.thankyou_phone.trim() } : {}),
      });
      toast.success("Thank-you page saved");
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't save the thank-you page");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="admin-thank-you-modal">
      <div className="my-8 w-full max-w-4xl border-2 border-black bg-white shadow-[6px_6px_0_0_#000]">
        <div className="flex items-center gap-3 border-b-2 border-black px-4 py-3">
          <div>
            <div className="label-mono text-zinc-500">Thank-you page settings</div>
            <div className="font-chivo text-lg font-black leading-tight" data-testid="admin-ty-title">{data?.page.title || "Loading…"}</div>
          </div>
          <button type="button" className="ml-auto p-1 hover:bg-zinc-100" onClick={onClose} aria-label="Close" data-testid="admin-ty-close">
            <X size={18} weight="bold" />
          </button>
        </div>
        {!form ? <div className="label-mono p-6">Loading…</div> : (
          <div className="grid gap-6 p-4 md:grid-cols-2">
            <Controls data={data} form={form} setForm={setForm} options={options} />
            <PagePreview data={data} form={form} options={options} shared={shared} />
          </div>
        )}
        <div className="flex justify-end gap-2 border-t-2 border-black px-4 py-3">
          <button type="button" className={`${BTN} bg-white hover:bg-zinc-100`} onClick={onClose} data-testid="admin-ty-cancel">Cancel</button>
          <button type="button" className={`${BTN} bg-black text-white hover:bg-emerald-700`} onClick={save} disabled={!form || saving}
                  data-testid="admin-save-settings-btn">
            {saving ? "Saving…" : "Save thank-you page"}
          </button>
        </div>
      </div>
    </div>
  );
};
